<template>
  <div data-testid="hotel-offer-modal-step-3">
    <div class="tw:py-6">
      <div>
        <p class="tw:font-bold tw:mb-1">{{ $t('hotel_offers_preview') }}</p>
        <p class="tw:text-sm tw:mb-4" v-html="$t('hotel_offers_preview_info')" />
      </div>
      <div v-if="!isLoading" data-testid="hotel-offer-modal-preview">
        <div
          class="tw:border tw:border-gray-300 tw:rounded-t-xl tw:flex tw:flex-row tw:items-center tw:justify-between tw:bg-blue-100 tw:p-4 tw:gap-x-3"
          data-testid="hotel-offer-modal-preview-offer-type"
        >
          <div class="tw:flex tw:flex-row tw:items-center tw:gap-x-3">
            <img class="tw:h-[40px] tw:w-[40px]" :src="hotelOfferIcon" alt="Hotel Offer" />
            <div>
              <p v-if="offer.type === 'FREE'" class="tw:font-bold tw:text-gray-800">
                {{ translationObject[`ho_${offer.name}_title`] }}
              </p>
              <p v-if="offer.type === 'DISCOUNT' && selectedOfferType === 0" class="tw:font-bold tw:text-gray-800">
                {{ translationObject[`ho_${offer.name}_free_title`] }}
              </p>
              <p v-if="offer.type === 'DISCOUNT' && selectedOfferType === 1" class="tw:font-bold tw:text-gray-800">
                {{ translationObject[`ho_${offer.name}_discount_title`] }}
              </p>
              <p class="tw:text-sm tw:mb-1">
                {{ translationObject[`ho_${offer.name}_text`] }}
              </p>
              <p v-if="expirationDate" class="tw:text-xs tw:text-gray-600">
                {{ $t('hotel_offers_validity_date') }}<span class="tw:ml-1">{{ validityDate }}</span>
              </p>
            </div>
          </div>
          <div
            class="box-size-44 tw:text-nowrap tw:px-6 tw:py-2.5 tw:text-base tw:rounded-lg line-height-260 tw:text-center tw:transition-colors tw:duration-300 tw:font-bold tw:border tw:border-gray-600 tw:text-gray-800 tw:bg-white tw:font-bold tw:border tw:border-gray-600 tw:text-gray-800 tw:bg-white tw:no-underline tw:cursor-default"
            data-testid="hotel-offer-modal-preview-contact-badge"
          >
            {{ $t('hotel_offers_official_site_badge') }}
          </div>
        </div>
        <div
          class="tw:border tw:border-gray-300 tw:rounded-b-xl tw:border-t-0 tw:bg-white tw:flex tw:flex-row tw:items-center tw:p-4 tw:gap-x-3"
          data-testid="hotel-offer-modal-preview-contact-info"
        >
          <img class="tw:h-[40px] tw:w-[40px]" :src="accommodationIcon" alt="Accommodation Icon" />
          <div class="tw:flex tw:flex-col tw:mb-1">
            <div class="tw:flex tw:flex-row tw:gap-x-4">
              <p v-if="currentAccommodation.phone" class="tw:flex tw:text-blue-800 tw:text-sm tw:flex tw:items-center">
                <img class="tw:w-[24px] tw:h-[24px] tw:mr-1" :src="phoneIcon" alt="Phone" />
                {{ currentAccommodation.phone }}
              </p>
              <p v-if="currentAccommodation.email" class="tw:flex tw:text-blue-800 tw:text-sm tw:flex tw:items-center">
                <img class="tw:w-[24px] tw:h-[24px] tw:mr-1" :src="emailIcon" alt="Email" />{{
                  currentAccommodation.email
                }}
              </p>
            </div>
            <div v-if="address.length > 0">
              <p class="tw:flex tw:text-sm tw:flex tw:items-center">
                <img class="tw:w-[24px] tw:h-[24px] tw:mr-1" :src="locationIcon" alt="Location" />
                {{ address }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tw:flex tw:w-full tw:justify-center tw:items-center tw:h-[167px]">
        <SmallDotLoader />
      </div>

      <p class="tw:text-sm tw:text-gray-600 tw:mt-4">
        {{ $t('hotel_offers_footnote') }}
      </p>
      <div class="tw:flex tw:flex-col tw:gap-y-3 tw:mt-6">
        <label class="tw:flex tw:justify-start tw:items-center tw:text-sm tw:mb-0 tw:text-gray-800">
          <input
            :checked="responsibilityCheckbox"
            type="checkbox"
            class="tw:hover:cursor-pointer tw:ml-[2px] tw:mr-2"
            @change="emit('toggleResponsibilityCheckbox')"
            data-testid="hotel-offer-modal-responsibility-checkbox"
          />
          <span>{{ $t('hotel_offers_tickbox_1') }}</span>
        </label>
        <label class="tw:text-gray-800 tw:flex tw:justify-start tw:items-center tw:text-sm tw:mb-0">
          <input
            :checked="displayCheckbox"
            type="checkbox"
            class="tw:hover:cursor-pointer tw:ml-[2px] tw:mr-2"
            @change="emit('toggleDisplayCheckbox')"
            data-testid="hotel-offer-modal-display-checkbox"
          />
          <span>{{ $t('hotel_offers_tickbox_2') }}</span>
        </label>
      </div>
      <div
        v-if="type === 'replace'"
        class="tw:py-2 tw:px-3 tw:mt-6 tw:bg-orange-200 tw:rounded-xl tw:text-gray-800 tw:flex tw:items-center"
      >
        <ExclamationTriangleLine class="tw:mr-3 tw:size-5" />
        <p class="tw:text-sm">
          {{ $t('hoteL_offers_warning') }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'

import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import ExclamationTriangleLine from '@/components/icons/ExclamationTriangleLine.svg?component'

import { useAccommodationsByOrgId } from '@/layouts/queries'
import { useOfferTranslations } from '@/components/hotelOffers/queries'
import { getSnippetsForQuery } from '@/utils/hotelOffersUtils.ts'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const hotelOfferIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/offer.png`, import.meta.url)
const accommodationIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/accommodation.png`, import.meta.url)
const emailIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/email.png`, import.meta.url)
const locationIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/location.png`, import.meta.url)
const phoneIcon = new URL(`../../../assets/img/hotel-offers/icons/UI/phone.png`, import.meta.url)

const { language, selectedOrganisationId } = useSessionValues()
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const { formatDate, formatPercentage } = useFormatting()

const address = computed(() => {
  return `${currentAccommodation.value?.streetAddress}, ${currentAccommodation.value?.postalCode} ${currentAccommodation.value?.city}`
})

const emit = defineEmits(['toggleDisplayCheckbox', 'toggleResponsibilityCheckbox'])
const props = defineProps({
  expirationDate: Date,
  responsibilityCheckbox: Boolean,
  displayCheckbox: Boolean,
  selectedOfferType: Number,
  discountValue: Number,
  type: String,
  offer: Object,
})

const translationObject = computed(() =>
  (data.value ?? []).reduce((acc, item) => {
    acc[item.snippet] = item.translation
    return acc
  }, {})
)

const discountedNumberFormatted = computed(() => formatPercentage(props.discountValue, 0, 0))
const validityDate = computed(() =>
  formatDate(props.expirationDate, {
    timeZone: 'UTC',
  })
)

const { isLoading, data } = useOfferTranslations({
  snippets: getSnippetsForQuery([
    {
      offer: props.offer,
      discountValue: props.selectedOfferType === 0 ? null : props.discountValue,
      formattedDiscount: props.selectedOfferType === 0 ? null : discountedNumberFormatted.value,
    },
  ]),
  languageCode: language.value,
})
</script>
